import { forwardRef, useContext } from 'react'
import { makeContentId, makeTriggerId } from '../constants'
import {
  StyledSegmentedSkeleton,
  StyledSegmentedTrigger
} from '../styled-components'
import { SegmentedContext } from './segmented-context'
import PropTypes from 'prop-types'

const SegmentedTrigger = forwardRef((props, forwardedRef) => {
  const { children, value, disabled = false, ...triggerProps } = props
  const context = useContext(SegmentedContext)

  const triggerId = makeTriggerId(context.baseId, value)
  const contentId = makeContentId(context.baseId, value)

  const isSelected = value === context.value

  return (
    <StyledSegmentedTrigger
      active={isSelected}
      type="button"
      role="segmented"
      aria-selected={isSelected}
      aria-controls={contentId}
      data-loading={context.loading ? '' : undefined}
      data-state={isSelected ? 'active' : 'inactive'}
      data-disabled={disabled ? '' : undefined}
      disabled={disabled || context.disabled || context.loading}
      id={triggerId}
      ref={forwardedRef}
      onMouseDown={(event) => {
        props.onMouseDown && props.onMouseDown()
        // only call handler if it's the left button (mousedown gets triggered by all mouse buttons)
        // but not when the control key is pressed (avoiding MacOS right click)
        if (!disabled && event.button === 0 && event.ctrlKey === false) {
          context.onValueChange(value)
          triggerProps.onClick && triggerProps.onClick(event)
        } else {
          // prevent focus to avoid accidental activation
          event.preventDefault()
        }
      }}
      onKeyDown={(event) => {
        props.onKeyDown && props.onKeyDown()
        if ([' ', 'Enter'].includes(event.key)) context.onValueChange(value)
      }}
      onFocus={() => {
        props.onFocus && props.onFocus()
        // handle "automatic" activation if necessary
        // ie. activate tab following focus
        const isAutomaticActivation = context.activationMode !== 'manual'
        if (!isSelected && !disabled && isAutomaticActivation) {
          context.onValueChange(value)
        }
      }}
      {...triggerProps}
    >
      {context.loading
        ? (
        <StyledSegmentedSkeleton>label</StyledSegmentedSkeleton>
          )
        : (
        <>{children}</>
          )}
    </StyledSegmentedTrigger>
  )
})

SegmentedTrigger.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool
}

SegmentedTrigger.displayName = 'SegmentedTrigger'
export default SegmentedTrigger
